import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Plane, Ship, Truck, ArrowRight, Warehouse, FileCheck, 
  Globe, CheckCircle2, Phone, Mail, Users, Zap, X, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';

const ServicesPage = () => {
  const { t } = useTranslation();

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" }
  ];

  // Main services - coordination-focused descriptions
  const services = [
    {
      id: 'air',
      icon: Plane,
      slug: 'air-freight',
      image: '/assets/banner-air-cargo.jpg',
      title: t('servicesPage.servicesList.air.title'),
      tagline: t('servicesPage.servicesList.air.tagline'),
      description: t('servicesPage.servicesList.air.description'),
      benefits: t('servicesPage.servicesList.air.benefits', { returnObjects: true }),
      cta: t('servicesPage.servicesList.air.cta')
    },
    {
      id: 'ocean',
      icon: Ship,
      slug: 'ocean-freight',
      image: '/assets/banner-ocean-freight.jpg',
      title: t('servicesPage.servicesList.ocean.title'),
      tagline: t('servicesPage.servicesList.ocean.tagline'),
      description: t('servicesPage.servicesList.ocean.description'),
      benefits: t('servicesPage.servicesList.ocean.benefits', { returnObjects: true }),
      cta: t('servicesPage.servicesList.ocean.cta')
    },
    {
      id: 'land',
      icon: Truck,
      slug: 'road-freight',
      image: '/assets/banner-trucking.jpg',
      title: t('servicesPage.servicesList.road.title'),
      tagline: t('servicesPage.servicesList.road.tagline'),
      description: t('servicesPage.servicesList.road.description'),
      benefits: t('servicesPage.servicesList.road.benefits', { returnObjects: true }),
      cta: t('servicesPage.servicesList.road.cta')
    },
    {
      id: 'warehousing',
      icon: Warehouse,
      slug: 'warehousing',
      image: '/assets/banner-warehousing.jpg',
      title: t('servicesPage.servicesList.warehousing.title'),
      tagline: t('servicesPage.servicesList.warehousing.tagline'),
      description: t('servicesPage.servicesList.warehousing.description'),
      benefits: t('servicesPage.servicesList.warehousing.benefits', { returnObjects: true }),
      cta: t('servicesPage.servicesList.warehousing.cta')
    },
    {
      id: 'customs',
      icon: FileCheck,
      slug: 'customs',
      image: '/assets/banner-documentation.jpg',
      title: t('servicesPage.servicesList.customs.title'),
      tagline: t('servicesPage.servicesList.customs.tagline'),
      description: t('servicesPage.servicesList.customs.description'),
      benefits: t('servicesPage.servicesList.customs.benefits', { returnObjects: true }),
      cta: t('servicesPage.servicesList.customs.cta')
    }
  ];

  // End-to-end coordination section
  const coordination = {
    title: t('servicesPage.endToEndTitle'),
    description: t('servicesPage.endToEndDesc'),
    features: [
      { icon: Users, text: t('servicesPage.coordinationFeatures.dedicatedContact') },
      { icon: Globe, text: t('servicesPage.coordinationFeatures.partnerNetwork') },
      { icon: Zap, text: t('servicesPage.coordinationFeatures.proactiveCommunication') },
      { icon: CheckCircle2, text: t('servicesPage.coordinationFeatures.fullDocumentation') }
    ]
  };

  // What we do vs don't do
  const whatWeDo = t('servicesPage.whatWeDoList', { returnObjects: true });
  const whatWeDontDo = t('servicesPage.whatWeDoNotList', { returnObjects: true });

  return (
    <div className="min-h-screen bg-offwhite" data-testid="services-page">
      <SEO 
        titleKey="seo.services.title"
        descriptionKey="seo.services.description"
        path="/services"
        breadcrumbs={breadcrumbs}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 50 Q 25 25, 50 50 T 100 50' stroke='%23C7A24E' stroke-width='0.5' fill='none'/%3E%3C/svg%3E")`,
            backgroundSize: '200px 100px'
          }} />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block text-gold font-space text-sm tracking-widest uppercase pb-2 border-b-2 border-gold/60 mb-6">
              {t('servicesPage.badge')}
            </span>
            <h1 className="font-space text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('servicesPage.heroTitle')}
            </h1>
            <p className="text-platinum-light text-lg md:text-xl">
              {t('servicesPage.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Sections */}
      {services.map((service, index) => (
        <section 
          key={service.id}
          id={service.id}
          className={`py-20 md:py-28 ${index % 2 === 0 ? 'bg-white' : 'bg-offwhite'}`}
        >
          <div className="container-custom">
            <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? 'lg:order-2' : ''}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-navy-900 flex items-center justify-center">
                    <service.icon className="w-6 h-6 text-gold" />
                  </div>
                  <span className="text-gold text-sm font-semibold">{service.tagline}</span>
                </div>
                
                <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
                  {service.title}
                </h2>
                
                <p className="text-slate-600 text-lg mb-8 leading-relaxed">
                  {service.description}
                </p>
                
                <ul className="space-y-3 mb-8">
                  {Array.isArray(service.benefits) && service.benefits.map((benefit) => (
                    <li key={`${service.slug}-${benefit.slice(0, 20)}`} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="flex flex-wrap gap-4">
                  <Link to="/quote">
                    <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 hover:shadow-xl hover:shadow-gold/25 px-6 py-3 rounded-lg font-semibold transition-all duration-300">
                      {service.cta} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to={`/services/${service.slug}`}>
                    <Button variant="outline" className="border-2 border-navy-900/20 text-navy-900 hover:bg-navy-900/5 px-6 py-3 rounded-lg">
                      {t('servicesPage.learnMore')}
                    </Button>
                  </Link>
                </div>
              </motion.div>
              
              {/* Visual Card */}
              <motion.div
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={index % 2 === 1 ? 'lg:order-1' : ''}
              >
                <Card className="bg-navy-900 border-0 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="aspect-video relative overflow-hidden group">
                      <img
                        src={service.image}
                        alt={service.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy-900/95 via-navy-900/25 to-transparent" />
                      <div className="absolute bottom-6 left-6 right-6">
                        <p className="text-white text-xl font-space font-semibold">{service.title}</p>
                        <p className="text-platinum-light text-sm mt-1">{service.tagline}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      {/* Our Role - Transparency Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('servicesPage.transparency')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('servicesPage.clearAboutOurRole')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('servicesPage.transparencyDesc')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* What We Do */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-green-200 bg-green-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('servicesPage.whatWeDo')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(whatWeDo) && whatWeDo.map((item) => (
                      <li key={`wedo-${item.slice(0, 20)}`} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* What We Don't Do */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-slate-200 bg-slate-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-slate-400 flex items-center justify-center">
                      <X className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('servicesPage.whatWeDoNot')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(whatWeDontDo) && whatWeDontDo.map((item) => (
                      <li key={`wedont-${item.slice(0, 20)}`} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* End-to-End Coordination */}
      <section className="py-20 md:py-28 bg-navy-900">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
                {t('servicesPage.completeSolution')}
              </span>
              <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-4">
                {coordination.title}
              </h2>
              <p className="text-platinum-light text-lg mb-8">
                {coordination.description}
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {coordination.features.map((feature) => (
                  <div key={`coord-${feature.text.slice(0, 15)}`} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                      <feature.icon className="w-5 h-5 text-gold" />
                    </div>
                    <span className="text-white">{feature.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-white/10 backdrop-blur-sm border-0">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
                    <Globe className="w-10 h-10 text-gold" />
                  </div>
                  <p className="text-platinum-light">
                    {t('servicesPage.howItWorks')}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-2 justify-center">
                    <span className="px-3 py-1 bg-white/10 rounded-full text-sm text-white">Air</span>
                    <span className="px-3 py-1 bg-white/10 rounded-full text-sm text-white">Ocean</span>
                    <span className="px-3 py-1 bg-white/10 rounded-full text-sm text-white">Road</span>
                    <span className="px-3 py-1 bg-white/10 rounded-full text-sm text-white">Warehousing</span>
                    <span className="px-3 py-1 bg-white/10 rounded-full text-sm text-white">Customs</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('servicesPage.readyToSimplify')}
            </h2>
            <p className="text-slate-600 text-lg mb-8">
              {t('servicesPage.readyToSimplifyDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/quote">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 font-semibold px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl hover:shadow-gold/25 transition-all w-full sm:w-auto">
                  {t('servicesPage.requestAQuote')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 text-slate-600 text-sm">
              <a href="mailto:info@elementarycargo.com" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Mail className="w-4 h-4" /> info@elementarycargo.com
              </a>
              <a href="tel:+31625381824" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Phone className="w-4 h-4" /> +31 62 538 1824
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
